import z__default from 'zod';
import { nameSuffixSchema } from '../../enums.mjs';
import '../../types/actor/actor.mjs';
import { Address, partialAddressSchema, addressSchema } from '../../types/address/address.mjs';
import '../../types/credit-report/company-credit-report.mjs';
import '../../types/credit-report/individual-credit-report.mjs';
import '../../types/currency/currency.mjs';
import '../../types/entityRelation/entity-relation.mjs';
import '../../types/financial-formula/adjustable-value.mjs';
import '../../types/financial-formula/adjustable-values-update.mjs';
import '../../types/financial-formula/adjustment.mjs';
import '../../types/financial-formula/formula.mjs';
import '@inrev/core';
import '../../types/is-encrypted.mjs';
import '../../types/naics-codes/naics-codes.mjs';
import { lastName, firstName, individualContactId, phoneNumber, email, decryptedSSN } from '../../types/opaque.mjs';
import '../../types/surety/account/surety-account.types.mjs';
import '../../types/surety/application-extraction/application-extraction-output.mjs';
import '../../types/surety/application-extraction/application-extraction-webhook.mjs';
import '../../types/surety/bond/surety-bond.types.mjs';
import '../../types/surety/bond-form/issued/issued-surety-bond-form.types.mjs';
import '../../types/surety/bond-form/surety-bond-form.data.mjs';
import '../../types/surety/bond-request/bond-request-extraction-output.mjs';
import '../../types/surety/bond-request/bond-request-extraction-webhook.mjs';
import '../../types/surety/contract/contract-extraction-output.mjs';
import '../../types/surety/contract/contract-extraction-webhook.mjs';
import '../../types/surety/financial-statement/financial-period/balance-sheet-data.mjs';
import '../../types/surety/financial-statement/financial-period/financial-period-extraction-webhook.mjs';
import '../../types/surety/financial-statement/financial-period/income-statement-data.mjs';
import '../../types/surety/financial-statement/financial-period/income-statement-fields.mjs';
import '../../types/surety/financial-statement/financial-period/income-statement-with-adjustments.mjs';
import '../../types/surety/financial-statement/financial-period/line-item-group.mjs';
import '../../types/surety/financial-statement/financial-period/section-keys.mjs';
import '../../types/surety/financial-statement/financial-period/statement-metadata.mjs';
import '../../types/surety/financial-statement/financial-period/view-shapes.mjs';
import '../../types/surety/financial-statement/financial-statement-schemas.mjs';
import '../../types/surety/indemnity/indemnity-agreement.mjs';
import '../../types/surety/quote/surety-quote.types.mjs';
import '../../types/surety/wip-schedule/wip-schedule-data.mjs';
import '../../types/surety/wip-schedule/wip-schedule-extraction-webhook.mjs';
import '../../types/surety/work-in-progress-statement/surety-work-in-progress-statement.mjs';
import '../../types/user/user.types.mjs';
import '../../types/utils.mjs';

var Get;
(function (Get) {
    (function (Response) {
        Response.schema = z__default.object({
            id: individualContactId,
            type: z__default.literal("owner").optional(),
            firstName: firstName,
            middleInitial: z__default.string().optional(),
            lastName: lastName,
            suffix: nameSuffixSchema.optional(),
            address: Address.VerifiedAddress.schema.optional().meta({ partial: true }),
            ssn: decryptedSSN.optional().meta({ partial: true }),
            email: email.optional(),
            phone: phoneNumber.optional(),
            spouseId: individualContactId.optional(),
            ownsAHome: z__default.boolean().optional().meta({ partial: true }),
            creditReportPermission: z__default.boolean().optional(),
            includeInIndemnity: z__default.boolean().optional(),
        });
    })(Get.Response || (Get.Response = {}));
})(Get || (Get = {}));
var Create;
(function (Create) {
    (function (Request) {
        const baseIndividual = z__default.object({
            firstName: firstName,
            middleInitial: z__default.string().nullish(),
            lastName: lastName,
            suffix: nameSuffixSchema.nullish(),
            address: partialAddressSchema.nullish().meta({ partial: true }),
            email: email.nullish(),
            phone: phoneNumber.nullish(),
        });
        Request.ownerSchema = z__default.object({
            ...baseIndividual.shape,
            type: z__default.literal("owner"),
            address: addressSchema.nullish().meta({ partial: true }),
            ssn: decryptedSSN.nullish().meta({ partial: true }),
            ownsAHome: z__default.boolean().nullish().meta({ partial: true }),
            creditReportPermission: z__default.boolean(),
        });
        Request.individualSchema = Request.ownerSchema
            .extend({
            type: z__default.literal("owner").nullish(),
            creditReportPermission: z__default.boolean().nullish(),
            spouseId: individualContactId.nullish(),
        })
            .superRefine((data, ctx) => {
            if (data.type === "owner" && typeof data.creditReportPermission !== "boolean") {
                ctx.addIssue({
                    code: "custom",
                    message: "Required",
                    path: ["creditReportPermission"],
                });
            }
        });
        Request.schema = Request.individualSchema;
        Request.ownerCardSchema = z__default.object({
            firstName: z__default.string().min(1),
            lastName: z__default.string().min(1),
            address: addressSchema.nullish().meta({ partial: true }),
            email: z__default.string().nullish(),
            ssn: z__default.string().nullish().meta({ partial: true }),
            ownsAHome: z__default.boolean().nullish().meta({ partial: true }),
        });
    })(Create.Request || (Create.Request = {}));
    (function (FromSubmission) {
        (function (Request) {
            Request.schema = z__default
                .array(Create.Request.individualSchema)
                .min(1)
                .max(2)
                .superRefine((arr, ctx) => {
                if (!arr.some((ind) => ind.type === "owner")) {
                    ctx.addIssue({
                        code: "custom",
                        message: "At least one individual must be an owner",
                    });
                }
            });
        })(FromSubmission.Request || (FromSubmission.Request = {}));
    })(Create.FromSubmission || (Create.FromSubmission = {}));
})(Create || (Create = {}));
var Update;
(function (Update) {
    (function (Request) {
        Request.schema = Create.Request.schema;
    })(Update.Request || (Update.Request = {}));
})(Update || (Update = {}));

export { Create, Get, Update };
